import axios from 'axios';

// Always use relative paths - works on any domain (preview, custom domain, subdomains)
// Kubernetes ingress routes /api/* to backend on all domains
export const API_BASE = '';
export const API = '/api';

let currentTenantId = null;
let currentCompanyId = null;

export function setTenantHeader(tenantId) {
  currentTenantId = tenantId;
}

export function setCompanyHeader(companyId) {
  currentCompanyId = companyId;
}

// Axios interceptor: attach x-tenant + x-company-id headers to every request
axios.interceptors.request.use((config) => {
  if (currentTenantId) {
    config.headers['x-tenant'] = currentTenantId;
  }
  if (currentCompanyId) {
    config.headers['x-company-id'] = currentCompanyId;
  }
  return config;
});

// ============================================================================
// Auto refresh on 401: when any API call returns 401, try POST /api/auth/refresh
// once and retry the original request. This keeps the cashier logged-in all day
// without re-typing the password every time the access token expires.
// Single in-flight refresh is shared across concurrent requests via `refreshPromise`.
// ============================================================================
let refreshPromise = null;
const PUBLIC_PATHS = [
  '/api/auth/login',
  '/api/auth/register',
  '/api/auth/refresh',
  '/api/auth/logout',
  '/api/tenant/resolve',
  '/api/tenant/branding',
  '/api/tenant/manifest',
  '/api/public/'
];
axios.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const orig = error.config || {};
    const url = orig.url || '';
    const isPublic = PUBLIC_PATHS.some(p => url.includes(p));
    if (error.response?.status === 401 && !orig._retry && !isPublic) {
      orig._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = axios.post('/api/auth/refresh', {}, { withCredentials: true })
            .finally(() => { refreshPromise = null; });
        }
        await refreshPromise;
        // Re-issue the original request with the freshly minted cookies
        return axios(orig);
      } catch (e) {
        // Refresh failed — let the original 401 bubble up
        return Promise.reject(error);
      }
    }
    // iter156fh: 402 = tenant bloqueado por pendencia financeira -> volta para o login (mostra o aviso)
    if (error.response?.status === 402 && !isPublic && typeof window !== 'undefined' && !window.__blocked402) {
      window.__blocked402 = true;
      const slug = window.location.pathname.split('/')[1];
      if (slug && slug !== 'superadmin' && !window.location.pathname.endsWith('/login')) {
        window.location.href = `/${slug}/login`;
      }
    }
    // iter156as: intercepta erros de gateway (Cloudflare 520/521/522/523/524/525/502/503/504)
    // e substitui a mensagem crua HTML por uma amigavel em portugues. Tambem
    // dispara um evento global para o banner centralizado renderizar.
    try {
      const status = error.response?.status;
      const raw = error.response?.data;
      const isCloudflareHtml = typeof raw === 'string' && (
        raw.includes('cloudflare') || raw.includes('Cloudflare') ||
        raw.includes('origin web server') || raw.includes('<!DOCTYPE')
      );
      const isGateway = status && [502, 503, 504, 520, 521, 522, 523, 524, 525].includes(status);
      const isNetwork = error.code === 'ERR_NETWORK' || error.message === 'Network Error';
      if (isCloudflareHtml || isGateway || isNetwork) {
        // iter156fe: emissao fiscal (/invoices/{id}/issue) — o proxy cortou a conexao enquanto a SEFAZ processava.
        // A nota PODE ter sido autorizada: orientar "Consultar status" em vez de "servidor sob carga".
        const reqUrl = String(error.config?.url || '');
        const isFiscalIssue = /\/invoices\/[^/]+\/(issue|cancel|cce)/.test(reqUrl) || reqUrl.includes('/manifesto/');
        // iter156fn: consultas a servicos externos (CNPJ/CEP) — a falha e do provedor publico, nao do nosso servidor
        const isLookup = reqUrl.includes('/lookup/');
        const friendly = {
          code: 'GATEWAY_UNAVAILABLE',
          status: status || 0,
          title: isNetwork ? 'Sem conexao com o servidor' : (isLookup ? 'Consulta externa indisponivel' : (isFiscalIssue ? 'SEFAZ demorou a responder' : 'Servidor sob alta carga')),
          detail: isNetwork
            ? 'Verifique sua conexao com a internet e tente novamente.'
            : (isLookup
              ? 'O servico publico de consulta (Receita Federal / BrasilAPI / Correios) nao respondeu. O servidor do sistema esta funcionando normalmente. Tente novamente em alguns minutos ou preencha os dados manualmente.'
              : (isFiscalIssue
              ? 'A conexao foi encerrada antes da SEFAZ concluir. A nota pode ter sido autorizada mesmo assim: aguarde alguns segundos, atualize a lista e use "Consultar status" na nota antes de reenviar (evita duplicidade).'
              : 'O servidor esta demorando para responder ou temporariamente indisponivel. Aguarde alguns segundos e tente novamente.')),
        };
        try {
          if (!error.response) error.response = {};
          error.response.data = friendly;
        } catch (_) { /* ignore */ }
        try {
          window.dispatchEvent(new CustomEvent('app:gateway-error', { detail: friendly }));
        } catch (_) { /* SSR safety */ }
      }
    } catch (_) { /* interceptor never fails */ }
    return Promise.reject(error);
  }
);

export default axios;
